import { GEOMETRY_TYPE } from '@aus-platform/cesium';
import {
  Button,
  ButtonVariant,
  CheckBox,
  Divider,
  FormMessage,
  FormMessageVariant,
  IconIdentifier,
  Input,
  InputGroup,
  Spinner,
  toast,
  ToggleSwitch,
} from '@aus-platform/design-system';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import type { Point } from 'chart.js';
import { clone, has, isEmpty, isNil, keys, omit } from 'lodash';
import {
  selectActiveWorkspaceLayersByFeatureType,
  selectMap3dDataset,
  selectMap3DState,
  setCurrentActiveMapTool,
  setIsElevationProfileSelectLayersActive,
} from 'map-3d/shared/map-3d-slices';
import { FeatureInfoTool, LineEventType } from 'map-3d/shared/map-3d-tools';
import { ToolEvent } from 'map-3d/shared/map-3d-tools/enums';
import type { IFeatureInfoTool } from 'map-3d/shared/map-3d-tools/interfaces';
import React, { useEffect, useState } from 'react';
import { trackEvent } from 'shared/analytics';
import type { ElevationProfile, IterationListItem } from 'shared/api';
import {
  FeatureGeometryType,
  handleResponseMessage,
  useFeatureInfo,
  useGenerateElevationProfile,
  useIterationsList,
} from 'shared/api';
import { ErrorSlugs, EventPropertyKey, Events } from 'shared/enums';
import { Formatter } from 'shared/helpers';
import { useOnFirstMount } from 'shared/hooks';
import type { ElevationProfileChartData } from '..';
import { MapTool } from '../../../../map-3d-container/map-3d-tools';
import { Map3DNoTerrainSelected, MarkArea } from '../../../shared';
import { elevationProfileToastErrors } from './constants';
import { AreaOfInterest, ElevationFormMessage } from './enums';
import type {
  ElevationProfileFormMessageType,
  ElevationToolSidecardProps,
  IterationListType,
  SelectedIterationType,
} from './types';

export const ElevationToolSideCard: React.FC<ElevationToolSidecardProps> = ({
  elevationProfile,
  setElevationProfile,
  setIterationIds,
  setLineCoordinates,
}) => {
  // Selectors.
  const { currentActiveMapTool, cesiumProxy, aoiManager } =
    useAppSelector(selectMap3DState);
  const workspaceLineLayersCount = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(state, FeatureGeometryType.Line),
  ).length;

  // Drawing tools.
  const lineTool = aoiManager?.lineTool;

  // States.
  const [iterationList, setIterationList] = useState<IterationListType[]>([]);
  const [selectedIterations, setSelectedIterations] =
    useState<SelectedIterationType | null>(null);
  const [currentAreaOfInterest, setCurrentAreaOfInterest] =
    useState<AreaOfInterest>(AreaOfInterest.Draw);
  const [featureInfoTool, setFeatureInfoTool] = useState<IFeatureInfoTool>();
  const [formMessage, setFormMessage] =
    useState<ElevationProfileFormMessageType>(null);
  const [selectedLineFeature, setSelectedLineFeature] = useState<
    string | null
  >();
  const [isElevationProfileDisabled, setIsElevationProfileDisabled] = useState(
    (currentAreaOfInterest === AreaOfInterest.Draw &&
      !lineTool?.isAnyLineDrawn) ||
      (currentAreaOfInterest === AreaOfInterest.WorkspaceLineFeatures &&
        formMessage?.variant === FormMessageVariant.Error),
  );

  // Hooks.
  const { selectedTerrainIteration, selectedTerrainSite, selectedProject } =
    useAppSelector(selectMap3dDataset);
  const isFirstRender = useOnFirstMount();
  const dispatch = useAppDispatch();

  // Variables.
  const { showElevationProfileModal } = elevationProfile;

  // Api.
  const {
    data: iterationListResponse,
    isLoading: isLoadingIterationList,
    isSuccess: isSuccessIterationList,
    refetch: refetchIterationList,
  } = useIterationsList({ siteId: selectedTerrainSite?.id ?? '' }, false);

  const {
    mutate: sendGenerateElevationProfileRequest,
    isPending: isLoadingGenerateElevationProfile,
    data: generateElevationProfileResponse,
    isSuccess: isSuccessGenerateElevationProfile,
    isError: isErrorGenerateElevationProfile,
    error: generateElevationProfileError,
  } = useGenerateElevationProfile();

  const {
    data: featureInfoResponse,
    error: featureInfoErrorResponse,
    isSuccess: isSuccessFeatureInfo,
    isError: isErrorSuccessFeatureInfo,
  } = useFeatureInfo(selectedLineFeature ?? '', !!selectedLineFeature);

  // useEffects.
  useEffect(() => {
    const onLineCreated = () => {
      setIsElevationProfileDisabled(false);
    };

    lineTool?.addEventListener(LineEventType.LineCreated, onLineCreated);

    return () => {
      lineTool?.removeEventListener(LineEventType.LineCreated, onLineCreated);
      dispatch(setIsElevationProfileSelectLayersActive(false));
    };
  }, [lineTool]);

  // Clear out previously calculated elevation profile on changing terrain.
  useEffect(() => {
    if (selectedTerrainIteration && selectedTerrainIteration.id) {
      refetchIterationList();

      setSelectedIterations({
        [selectedTerrainIteration.id]: {
          name: selectedTerrainIteration.name,
        },
      });

      resetElevationProfile();
      lineTool?.deleteAllLines();
      lineTool?.deactivate();
    }
  }, [selectedTerrainIteration]);

  useEffect(() => {
    if (isSuccessIterationList && iterationListResponse) {
      setIterationList(
        // Here we need all the iterations except the
        // one that we've already selected in dataset
        iterationListResponse.list.filter(
          (iteration: IterationListItem) =>
            selectedTerrainIteration?.id !== iteration.id,
        ),
      );
    }
  }, [isSuccessIterationList, iterationListResponse]);

  useEffect(() => {
    if (isFirstRender) {
      return;
    }

    let updatedElevationProfile = clone(elevationProfile);
    if (isSuccessGenerateElevationProfile && generateElevationProfileResponse) {
      const { elevationProfiles, distancePoints } =
        generateElevationProfileResponse.data;

      const iterations: string[] = [];
      const elevationsList: Point[][] = [];

      elevationProfiles.forEach((elevationProfile: ElevationProfile) => {
        const { iteration, elevations } = elevationProfile;
        const coordinates: Point[] = [];
        elevations.forEach((elevation) => {
          coordinates.push({ x: elevation[0], y: elevation[1] });
        });

        elevationsList.push(coordinates);
        if (!isNil(selectedIterations)) {
          iterations.push(selectedIterations[iteration].name);
        }
      });

      const elevationChartData: ElevationProfileChartData = {
        distancePoints,
        elevationsList,
        iterations,
      };

      updatedElevationProfile = {
        ...updatedElevationProfile,
        elevationProfileChartData: elevationChartData,
        elevationProfileError: null,
        isElevationProfileModalEmpty: false,
        isElevationProfileModalLoading: false,
      };
    } else if (isErrorGenerateElevationProfile) {
      // Adding error message to elevation profile.
      const errorSlug = generateElevationProfileError?.meta.slug;

      updatedElevationProfile = {
        ...updatedElevationProfile,
        isElevationProfileModalEmpty: true,
        elevationProfileError:
          errorSlug === ErrorSlugs.LineOutOfBounds
            ? elevationProfileToastErrors[errorSlug]
            : null,
      };
    } else if (isLoadingGenerateElevationProfile) {
      updatedElevationProfile.isElevationProfileModalLoading = true;
      toast.info('Calculating elevation.');
    }

    setElevationProfile(updatedElevationProfile);

    handleResponseMessage(
      isSuccessGenerateElevationProfile,
      isErrorGenerateElevationProfile,
      generateElevationProfileResponse,
      generateElevationProfileError,
    );
  }, [
    isSuccessGenerateElevationProfile,
    generateElevationProfileResponse,
    isErrorGenerateElevationProfile,
    isLoadingGenerateElevationProfile,
  ]);

  useEffect(() => {
    if (isSuccessFeatureInfo && featureInfoResponse && lineTool) {
      const activeLineStyleOptions = {
        lineStyleOptions: {
          strokeColor: '#3f95fd',
        },
      };

      lineTool.importWKT(
        featureInfoResponse.data.geometry,
        featureInfoResponse.data.id,
        { elevationProfileSelectedLine: true },
        undefined,
        activeLineStyleOptions,
      );
    }
    handleResponseMessage(
      isSuccessFeatureInfo,
      isErrorSuccessFeatureInfo,
      featureInfoResponse,
      featureInfoErrorResponse,
    );
  }, [
    isSuccessFeatureInfo,
    featureInfoResponse,
    isErrorSuccessFeatureInfo,
    featureInfoErrorResponse,
  ]);

  useEffect(() => {
    if (!cesiumProxy?.viewer) {
      return;
    }

    const featureInfoTool = new FeatureInfoTool(cesiumProxy.cesiumViewer);
    setFeatureInfoTool(featureInfoTool);

    // Event Listeners declared inside useEffect to remove them properly.
    const onFeatureFocusListener = ([featureId], type) => {
      if (currentAreaOfInterest !== AreaOfInterest.WorkspaceLineFeatures) {
        return;
      }

      lineTool?.deleteAllLines();

      // Clear out previous form messages.
      setFormMessage(null);

      if (type !== GEOMETRY_TYPE.LINE) {
        setFormMessage({
          variant: FormMessageVariant.Error,
          message: ElevationFormMessage.NotLineFeature,
        });
      } else {
        setSelectedLineFeature(featureId);
      }
    };

    const onFeatureBlurListener = () => {
      setFormMessage(null);
    };

    featureInfoTool.addEventListener(ToolEvent.Blur, onFeatureBlurListener);
    featureInfoTool.addEventListener(ToolEvent.Focus, onFeatureFocusListener);

    return () => {
      featureInfoTool.removeEventListener(
        ToolEvent.Blur,
        onFeatureBlurListener,
      );
      featureInfoTool.removeEventListener(
        ToolEvent.Focus,
        onFeatureFocusListener,
      );
    };
  }, [cesiumProxy?.viewer, currentAreaOfInterest]);

  useEffect(() => {
    if (
      !isNil(selectedLineFeature) &&
      currentAreaOfInterest === AreaOfInterest.WorkspaceLineFeatures
    ) {
      drawElevationGraph();
    }

    setIsElevationProfileDisabled(isNil(selectedLineFeature));
  }, [selectedLineFeature, isSuccessFeatureInfo]);

  // Handlers.
  const onSelectIterationHandler = (selectedIteration: IterationListType) => {
    // Checking if the key exists in the object or not.
    if (!has(selectedIterations, selectedIteration.id)) {
      setSelectedIterations((prev) => ({
        ...prev,
        [selectedIteration.id]: {
          name: selectedIteration.name,
        },
      }));
    } else {
      // Remove the object which has selectLayer.id as key.
      const remainingIterations = omit(selectedIterations, [
        selectedIteration.id,
      ]);
      if (isEmpty(remainingIterations)) {
        setSelectedIterations(null);
      } else {
        setSelectedIterations(remainingIterations);
      }
    }
  };

  const resetElevationProfile = () => {
    if (lineTool?.isAnyLineDrawn) {
      lineTool?.deleteAllLines();
      setElevationProfile({
        ...elevationProfile,
        isElevationProfileModalEmpty: true,
        elevationProfileError: null,
        elevationProfileChartData: null,
      });
    }
  };

  const drawHandler = () => {
    resetElevationProfile();
    dispatch(setCurrentActiveMapTool(MapTool.None));

    lineTool?.activate(
      {
        elevationProfileLine: true,
      },
      {},
      true,
    );

    // ELevation profile should be disabled if no line is drawn.
    setIsElevationProfileDisabled(true);
    lineTool?.enableSingleLineDrawing();
  };

  const drawElevationGraph = () => {
    if (!selectedIterations || !lineTool) {
      return;
    }

    if (
      !lineTool?.isAnyLineDrawn &&
      currentAreaOfInterest === AreaOfInterest.Draw
    ) {
      toast.error('Please draw a line first.');
      return;
    }

    const selectedIterationsIds = keys(selectedIterations);

    if (!isNil(selectedLineFeature)) {
      const positions = lineTool
        .getLinesByProperty('elevationProfileSelectedLine', true)
        .map(lineTool.getCartographicPositions)[0];

      setLineCoordinates({
        featureId: selectedLineFeature,
        coordinateArray: positions
          ? [positions[0], positions[positions.length - 1]]
          : undefined,
      });

      sendGenerateElevationProfileRequest({
        iterations: selectedIterationsIds,
        feature: selectedLineFeature,
      });
    } else {
      const lineWkt = lineTool.exportCurrentLinesToWKT()[0].wkt ?? '';
      const positions = lineTool
        .getLinesByProperty('elevationProfileLine', true)
        .map(lineTool.getCartographicPositions)[0];

      setLineCoordinates({
        wkt: lineWkt,
        coordinateArray: positions
          ? [positions[0], positions[positions.length - 1]]
          : undefined,
      });

      sendGenerateElevationProfileRequest({
        iterations: selectedIterationsIds,
        lineWkt,
      });
    }

    setIterationIds(selectedIterationsIds);

    setElevationProfile({
      ...elevationProfile,
      showElevationProfileModal: true,
      isElevationProfileModalEmpty: false,
      isElevationProfileModalLoading: true,
    });

    trackEvent(Events.GenerateProfileClicked, {
      [EventPropertyKey.ProjectName]: selectedProject?.name,
      [EventPropertyKey.SiteName]: selectedTerrainSite?.name,
      [EventPropertyKey.IterationName]: selectedTerrainIteration?.name,
    });
  };

  const handleOpenGraphViewer = () => {
    setElevationProfile({
      ...elevationProfile,
      showElevationProfileModal: true,
    });

    trackEvent(Events.OpenGraphViewerClicked, {
      [EventPropertyKey.ProjectName]: selectedProject?.name,
      [EventPropertyKey.SiteName]: selectedTerrainSite?.name,
      [EventPropertyKey.IterationName]: selectedTerrainIteration?.name,
    });
  };

  const onToggleChange = () => {
    if (
      currentActiveMapTool === MapTool.FeatureTool &&
      currentAreaOfInterest === AreaOfInterest.Draw
    ) {
      return;
    }

    // Reset previously calculated elevation profile.
    resetElevationProfile();
    setCurrentAreaOfInterest((prevState) => {
      // Select feature.
      if (prevState === AreaOfInterest.Draw) {
        dispatch(setIsElevationProfileSelectLayersActive(true));
        featureInfoTool?.activate();

        setFormMessage({
          message: ElevationFormMessage.SelectFeature,
          variant: FormMessageVariant.Info,
        });

        if (workspaceLineLayersCount === 0) {
          setFormMessage({
            message: ElevationFormMessage.NoLineErrors,
            variant: FormMessageVariant.Error,
          });
        }
      }
      // Draw elevation line mode.
      else {
        lineTool?.deleteAllLoadedLines();
        dispatch(setIsElevationProfileSelectLayersActive(false));
        featureInfoTool?.deactivate();
        setFormMessage(null);
        setSelectedLineFeature(null);
      }

      return prevState === AreaOfInterest.Draw
        ? AreaOfInterest.WorkspaceLineFeatures
        : AreaOfInterest.Draw;
    });
  };

  // Render.
  return (
    <div className="map-3d-elevation-container">
      {selectedTerrainIteration?.id ? (
        <>
          <div className="map-3d-elevation-content">
            <InputGroup>
              <Input.Label className="map-3d-elevation__section-header">
                Active Dataset
              </Input.Label>
              <Input.Label>Site</Input.Label>
              <Input.Text value={selectedTerrainSite?.name} readOnly disabled />
              <Input.Label>Iteration</Input.Label>
              <Input.Text
                value={selectedTerrainIteration?.name}
                readOnly
                disabled
              />
            </InputGroup>
            <div className="map-3d-elevation__compare-iteration__container">
              <Input.Label className="map-3d-elevation__section-header">
                Select Iteration(s) to compare
              </Input.Label>
              <div className="map-3d-elevation__compare-iteration">
                {isLoadingIterationList ? (
                  <Spinner />
                ) : iterationList.length === 0 ? (
                  <div className="map-3d-elevation__compare-iteration__empty-list">
                    No iterations to compare.
                  </div>
                ) : (
                  iterationList.map((iteration, index: number) => {
                    return (
                      <CheckBox
                        key={index}
                        title={iteration.name}
                        onClick={() => onSelectIterationHandler(iteration)}
                        checked={!isNil(selectedIterations?.[iteration.id])}
                        showCard={true}
                      />
                    );
                  })
                )}
              </div>
            </div>
            <InputGroup className="map-3d-elevation__mark-area">
              <Input.Label className="map-3d-elevation__section-header">
                Mark Area of Interest
              </Input.Label>

              <ToggleSwitch
                leftTitle={Formatter.toTitleCase(MarkArea.Draw, '_')}
                rightTitle={Formatter.toTitleCase(
                  MarkArea.SelectLineFeature,
                  '_',
                )}
                leftSelected={currentAreaOfInterest === AreaOfInterest.Draw}
                onChange={onToggleChange}
              />
            </InputGroup>
            {currentAreaOfInterest === AreaOfInterest.Draw && (
              <Button
                className="map-3d-elevation__mark-area__draw-line-btn"
                variant={ButtonVariant.Outline}
                leftIconIdentifier={IconIdentifier.Line}
                onClick={drawHandler}
              >
                Start Drawing
              </Button>
            )}
            {formMessage && (
              <FormMessage
                message={formMessage.message}
                variant={formMessage.variant}
              />
            )}
          </div>
          <Divider text="" />
          <div className="map-3d-elevation__footer">
            <Button
              variant={ButtonVariant.Secondary}
              onClick={handleOpenGraphViewer}
              disabled={showElevationProfileModal}
            >
              Open Graph Viewer
            </Button>
            <Button
              onClick={drawElevationGraph}
              isLoading={isLoadingGenerateElevationProfile}
              data-event="generate_elevation_profile_event"
              disabled={
                isElevationProfileDisabled || isLoadingGenerateElevationProfile
              }
            >
              Generate Profile
            </Button>
          </div>
        </>
      ) : (
        <Map3DNoTerrainSelected />
      )}
    </div>
  );
};
